import React from 'react';
import { useGetProcurementDemandItemsQuery } from '../../../store/endpoints/ProcurementEndpoints';
import { Checkbox, ICommandBarItemProps, IconButton, Shimmer } from '@fluentui/react';
import { IProcurementDemandItem } from '../../../models/Procurement';
import Widget from '../../Common/Widgets/Widget';

interface IProcurementDemandsWidgetProps {
    onDemandItemClick: (itemId: string) => void;
    onStorefrontOrderClick: (orderId: string) => void;
    onReSourceClick?: (orderIds: string[]) => void;
    onCreateOrderClick?: (demands: IProcurementDemandItem[]) => void;
    size?: 'small' | 'medium' | 'large';
    vendorFilter?: string;
    onFullscreenClick?: () => void;
}

const ProcurementDemandsWidget: React.FC<IProcurementDemandsWidgetProps> = (props) => {
    const [selected, setSelected] = React.useState<string[]>([]);
    const [selectedVendor, setSelectedVendor] = React.useState<string | null>(null);
    const demandItems = useGetProcurementDemandItemsQuery({ conditions: {
        filters: {
            status: 'New',
            ...(props.vendorFilter ? { vendor: props.vendorFilter } : {})
        }
    }})
    const [globalChecked, setGlobalChecked] = React.useState(false);
    const [indeterminate, setIndeterminate] = React.useState(false);
    const [showAllCheckboxes, setShowAllCheckboxes] = React.useState(false);

    React.useEffect(() => {
        if (selected.length === 0) {
            setGlobalChecked(false);
            setIndeterminate(false);
            setShowAllCheckboxes(false);
        } else if (selected.length === (demandItems.data?.items.filter(i => i.vendor === selectedVendor).length || 0)) {
            setGlobalChecked(true);
            setIndeterminate(false);
            setShowAllCheckboxes(true);
        } else {
            setGlobalChecked(false);
            setIndeterminate(true);
            setShowAllCheckboxes(true);
        }
    }, [selected, selectedVendor]);

    const refetch = () => {
        demandItems.refetch();
        setSelected([]);
        setSelectedVendor(null);
    }

    const handleGlobalCheckboxChange = (ev?: React.FormEvent<HTMLElement | HTMLInputElement> , checked?: boolean) => {
        // Because we want the next state to be checked if indeterminate, we ignore the current state and just use the new checked value
        if (selectedVendor !== null && !globalChecked) {
            const toSelect = demandItems.data?.items.filter(i => i.vendor === selectedVendor).map(i => i.id ?? '') || [];
            setSelected(toSelect);
        } else {
            setSelected([]);
            setSelectedVendor(null);
        }
    }

    const farCommands: ICommandBarItemProps[] = [
        {
            key: 'resource',
            text: 'Re-Source Selected',
            iconOnly: true,
            iconProps: { iconName: 'DeliveryTruck' },
            disabled: selected.length === 0,
            onClick: () => props.onReSourceClick && props.onReSourceClick(selected),
        },
        {
            key: 'order',
            text: 'Create Order',
            iconOnly: true,
            iconProps: { iconName: 'ShoppingCart' },
            disabled: selected.length === 0,
            onClick: () => props.onCreateOrderClick?.(
                demandItems.data?.items.filter(i => selected.includes(i.id ?? '')) ?? []
            ),
        },
        {
            key: 'refresh',
            iconOnly: true,
            iconProps: { iconName: 'Refresh' },
            onClick: refetch,
        }
    ]

    if (props.onFullscreenClick) {
        farCommands.push({
            key: 'fullscreen',
            iconOnly: true,
            iconProps: { iconName: 'OpenInNewWindow' },
            onClick: props.onFullscreenClick,
        })
    }

    return (
        <Widget
            header='Procurement Demands'
            rightCommands={farCommands}
            table={true}
            tableColspan={8}
            loading={demandItems.isFetching}
            shimmerRow={<RefetchingRow />}
            size={props.size}
            tableHeaderRow={
                <tr>
                    <th style={{width: '40px'}}>
                        <Checkbox
                            styles={{checkbox: { display: showAllCheckboxes ? undefined : 'none' }}}
                            checked={globalChecked}
                            onChange={handleGlobalCheckboxChange}
                            indeterminate={indeterminate}
                        />
                    </th>
                    <th style={{width: '150px'}}>Vendor</th>
                    <th style={{width: '150px'}}>Manufacturer</th>
                    <th>Item</th>
                    <th style={{width: '50px'}}>Quantity</th>
                    <th style={{width: '100px'}}>Source</th>
                    <th style={{width: '100px'}}>Customer</th>
                    <th style={{width: '25px'}} />
                </tr>
            }
        >
            {demandItems.data?.items.map(item => (
                <DemandItemRow
                    key={item.id}
                    item={item}
                    selected={selected.includes(item.id ?? '')}
                    onSelectedChange={(isSelected) => {
                        if (isSelected) {
                            setSelected([...selected, item.id ?? '']);
                            setSelectedVendor(item.vendor ?? null);
                        } else {
                            setSelected(selected.filter(s => s !== item.id));
                            if (selected.filter(s => s !== item.id).length === 0) {
                                setSelectedVendor(null);
                            }
                        }
                    }}
                    disabled={selectedVendor !== null && selectedVendor !== item.vendor}
                    onDetailClick={() => props.onDemandItemClick(item.id ?? '')}
                    showCheckbox={showAllCheckboxes}
                    onStorefrontOrderClick={props.onStorefrontOrderClick}
                />
            ))}
        </Widget>
    )
}

const RefetchingRow: React.FC = () => <tr>
    <td></td>
    <td><Shimmer /></td>
    <td><Shimmer /></td>
    <td><Shimmer /></td>
    <td><Shimmer /></td>
    <td><Shimmer /></td>
    <td><Shimmer /></td>
    <td></td>
</tr>;

interface IDemandItemRowProps {
    item: IProcurementDemandItem;
    selected: boolean;
    onSelectedChange: (selected: boolean) => void;
    disabled: boolean;
    onDetailClick: () => void;
    onStorefrontOrderClick: (orderId: string) => void;
    showCheckbox: boolean;
}

const DemandItemRow: React.FC<IDemandItemRowProps> = (props) => {
    const [hovering, setHovering] = React.useState(false);

    const source = () => {
        if (props.item.storefrontOrderId) {
            return <span 
            style={{color: '#0064b0ff', cursor: 'pointer', fontWeight: 'bold', marginRight: '0.5em'}}
            onClick={() => props.onStorefrontOrderClick(props.item.storefrontOrderId ?? '')}
            >
                Storefront
            </span>

        }
        return '';
    }

    return (
        <tr style={{minHeight: '50px'}} onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)}>
            <td style={{minHeight: '40px'}}>
                <Checkbox
                    styles={{checkbox: { display: props.showCheckbox || hovering ? undefined : 'none' }}}
                    disabled={props.disabled}
                    checked={props.selected}
                    onChange={(_, checked) => props.onSelectedChange(!!checked)}
                />
            </td>
            <td>{props.item.vendor}</td>
            <td>{props.item.manufacturer}</td>
            <td>{props.item.productName}</td>
            <td>{props.item.quantity}</td>
            <td>{source()}</td>
            <td>{props.item.customerEmail}</td>
            <td>
                <IconButton iconProps={{ iconName: 'EntryView'}} onClick={props.onDetailClick} />
            </td>
        </tr>
    );
}

export default ProcurementDemandsWidget;